import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flex, Input } from 'antd';
import type { GetProps } from 'antd';
import { submitReset, submitResetPasswordEmail, submitVerifyOTP } from "../function";

interface ForgetPasswordProps {
    email?: string;
}

type OTPProps = GetProps<typeof Input.OTP>;
const ForgotPassword: React.FC<ForgetPasswordProps> = ({ email = '' }) => {
    const { t } = useTranslation();
    const [forgetEmail, setForgetEmail] = useState(email);
    const [resetToken, setResetToken] = useState('');

    const [step, setStep] = useState(1);

    const [otpValue, setOtpValue] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [resetPasswordCheckbox, setResetPasswordCheckbox] = useState(false);
    
    useEffect(() => {
        const handleVerifyOTP = async () => {
            await submitVerifyOTP(forgetEmail, otpValue).then((token) => {
                if (token) {
                    setResetToken(token);
                    setStep(3);
                }
            });
        };
        if (otpValue.length === 6) {
            handleVerifyOTP();
        }
    }, [otpValue]);
    const onChange: OTPProps['onChange'] = async (text) => {
        setOtpValue(text);
    };

    const onInput: OTPProps['onInput'] = (value) => {
        // change every input
    };

    const sharedProps: OTPProps = {
        onChange,
        onInput,
    };


    return (
        <div className=" max-w-[500px] mx-auto card card-body bg-white" style={{
            boxShadow: '0 0.5rem 1rem rgba(2, 2, 76, 0.05)',
            border: 'none',
            padding: '2.25rem',
            borderRadius: '1rem',
        }}>
            <div className="text-center mb-2">
                <img src="/images/logo/logoweb.webp" alt="abc logo" className="mx-auto mb-2 w-16 h-full" />
                <h5 className="text-lg font-semibold">{t('auth.welcome')}</h5>
                {step === 1 && <p className="text-sm text-text-secondary">{t('auth.forgot_desc')}</p>}
                {step === 2 && <p className="text-sm text-text-secondary">{t('auth.otp_desc')}</p>}
                {step === 3 &&
                    <p className="text-sm text-text-secondary">{t('auth.new_password_desc')}</p>
                }
            </div>
            <div>
                {step === 1 && (
                    <>
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-text-tertiary mb-1" htmlFor="registerEmail">{t('auth.email')}</label>
                            <input
                            value={forgetEmail} onChange={(e) => setForgetEmail(e.target.value)}
                            type="email" className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-3" id="registerEmail" placeholder={t('auth.email_placeholder')} />
                        </div>
                        <div className="mb-3 flex flex-col gap-2">
                            <button type="button"
                            disabled={forgetEmail.length === 0}
                            onClick={async () => {
                                const status = await submitResetPasswordEmail(forgetEmail);
                                if (status) {
                                    setStep(2);
                                }
                            }} value="Submit" className="w-full !mb-2 bg-primary hover:bg-primary/90 !text-white font-semibold !py-3 px-4 rounded">{t('auth.submit')}</button>
                            <Link to="/login" className="w-full">
                                <button type="button" className="w-full bg-gray-200 hover:bg-gray-300 !text-gray-900 font-semibold !py-3 px-4 rounded">{t('auth.back_to_login')}</button>
                            </Link>
                        </div>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Flex justify="center" align="center" gap={16}>
                            <Input.OTP
                                {...sharedProps}
                                value={otpValue}
                                onChange={(value) => setOtpValue(value)}
                                length={6}
                                size="large"
                                autoFocus
                            />
                        </Flex>
                        <div className="mt-4 text-center">
                            <button type="button" className="text-sm text-primary hover:underline" onClick={async () => {
                                await submitResetPasswordEmail(forgetEmail);
                            }}>{t('auth.resend_otp')}</button>
                        </div>
                    </>
                )}
                {step === 3 && (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="newPassword">{t('auth.new_password')}</label>
                            <div className="relative">
                                <input type={
                                    showPassword ? 'text' : 'password'
                                } className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2 pr-10" id="registerPassword" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} />
                                <button type="button" id="togglePassword" className="absolute right-2 top-1/2 -translate-y-1/2 !text-gray-500 hover:!text-gray-700" aria-pressed="false" aria-label="Show password" title="Show password">
                                    <button onClick={() => setShowPassword(!showPassword)} className="flex items-center justify-center w-8 h-8 hover:text-gray-700">
                                        {showPassword ?
                                            (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z"></path></svg>
                                            ) :
                                            (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M53.92,34.62A8,8,0,1,0,42.08,45.38L61.32,66.55C25,88.84,9.38,123.2,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208a127.11,127.11,0,0,0,52.07-10.83l22,24.21a8,8,0,1,0,11.84-10.76Zm47.33,75.84,41.67,45.85a32,32,0,0,1-41.67-45.85ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.16,133.16,0,0,1,25,128c4.69-8.79,19.66-33.39,47.35-49.38l18,19.75a48,48,0,0,0,63.66,70l14.73,16.2A112,112,0,0,1,128,192Zm6-95.43a8,8,0,0,1,3-15.72,48.16,48.16,0,0,1,38.77,42.64,8,8,0,0,1-7.22,8.71,6.39,6.39,0,0,1-.75,0,8,8,0,0,1-8-7.26A32.09,32.09,0,0,0,134,96.57Zm113.28,34.69c-.42.94-10.55,23.37-33.36,43.8a8,8,0,1,1-10.67-11.92A132.77,132.77,0,0,0,231.05,128a133.15,133.15,0,0,0-23.12-30.77C185.67,75.19,158.78,64,128,64a118.37,118.37,0,0,0-19.36,1.57A8,8,0,1,1,106,49.79,134,134,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41A8,8,0,0,1,247.31,131.26Z"></path></svg>
                                            )
                                        }
                                    </button>
                                </button>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="confirmNewPassword">{t('auth.confirm_new_password')}</label>
                            <div className="relative">
                                <input type={
                                    showConfirmPassword ? 'text' : 'password'
                                } className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2 pr-10" id="registerPassword" placeholder="********" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                <button type="button" id="togglePassword" className="absolute right-2 top-1/2 -translate-y-1/2 !text-gray-500 hover:!text-gray-700" aria-pressed="false" aria-label="Show password" title="Show password">
                                    <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="flex items-center justify-center w-8 h-8 hover:text-gray-700">
                                        {showConfirmPassword ?
                                            (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z"></path></svg>
                                            ) :
                                            (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M53.92,34.62A8,8,0,1,0,42.08,45.38L61.32,66.55C25,88.84,9.38,123.2,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208a127.11,127.11,0,0,0,52.07-10.83l22,24.21a8,8,0,1,0,11.84-10.76Zm47.33,75.84,41.67,45.85a32,32,0,0,1-41.67-45.85ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.16,133.16,0,0,1,25,128c4.69-8.79,19.66-33.39,47.35-49.38l18,19.75a48,48,0,0,0,63.66,70l14.73,16.2A112,112,0,0,1,128,192Zm6-95.43a8,8,0,0,1,3-15.72,48.16,48.16,0,0,1,38.77,42.64,8,8,0,0,1-7.22,8.71,6.39,6.39,0,0,1-.75,0,8,8,0,0,1-8-7.26A32.09,32.09,0,0,0,134,96.57Zm113.28,34.69c-.42.94-10.55,23.37-33.36,43.8a8,8,0,1,1-10.67-11.92A132.77,132.77,0,0,0,231.05,128a133.15,133.15,0,0,0-23.12-30.77C185.67,75.19,158.78,64,128,64a118.37,118.37,0,0,0-19.36,1.57A8,8,0,1,1,106,49.79,134,134,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41A8,8,0,0,1,247.31,131.26Z"></path></svg>
                                            )
                                        }
                                    </button>
                                </button>
                            </div>
                        </div>
                        <div className="mb-3">
                            <div className="flex items-center !space-x-2">
                                <input
                                checked={resetPasswordCheckbox} onChange={(e) => setResetPasswordCheckbox(e.target.checked)}
                                className="h-4 w-4 !text-primary !no-underline border-gray-300 !rounded" type="checkbox" id="termsConditions" name="terms" />
                                <label className="text-sm text-gray-700 dark:text-gray-300" htmlFor="termsConditions">
                                    {t('auth.terms_agree')} <a href="" className="!text-primary !no-underline">{t('auth.terms_conditions')}</a>
                                </label>
                            </div>
                        </div>
                        <div className="mb-3">
                            <button
                                disabled={password.length === 0 || confirmPassword.length === 0 || password !== confirmPassword || !resetPasswordCheckbox}
                                onClick={async () => {
                                    const status = await submitReset(forgetEmail, resetToken, password , confirmPassword);
                                }}
                            type="button" value="Submit" className="w-full bg-primary hover:bg-primary/90 text-white font-semibold !py-2 px-4 rounded">{t('auth.reset_password')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ForgotPassword;